<template>
  <el-table-column
    v-if="visible"
    v-bind="$attrs"
    :type="type"
    :label="label"
    :width="width"
    :class-name="className"
  >
    <template v-if="type !== 'selection'" #default="scope">
      <slot
        v-if="slots.default"
        v-bind="getSafeScope(scope)"
      />
      <span v-else>
        {{ formatMaybeNumber(getSafeValue(scope.row, attrs.prop)) }}
      </span>
    </template>
  </el-table-column>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, useSlots, useAttrs, inject } from 'vue';
import { store } from '@/store';
import { numberFormatter } from "@/lib/utils";
defineOptions({
  name: 'BaseTableColumn',
})

const props = defineProps({
  defaultVisible: { type: [Boolean, String], default: true },
  label: { type: String, default: '' },
  width: { type: [String, Number], default: null },
  className: { type: String, default: '' },
  type: { type: String, default: '' },
  // Если не задано, берется значение из BaseTable (provide)
  formatNumbers: { type: Boolean, default: false },
  // Исключить колонку из селектора "Колонки" (но оставить в таблице)
  includeInColumns: { type: Boolean, default: true },
  // Настраиваемый идентификатор колонки для селектора
  columnId: { type: [String, Number], default: null }
});

const slots = useSlots();
const attrs = useAttrs();
const instance = getCurrentInstance();

const getSafeScope = (scope) => {
  return {
    row: scope?.row || {},
    column: scope?.column || {},
    $index: scope?.$index ?? -1
  }
}

const getSafeValue = (row, prop) => {
  if (!row || !prop) return '';
  return prop.split('.').reduce((acc, key) => acc?.[key], row) ?? '';
};

// Наследуем настройку из BaseTable, если в колонке явно не указано
const injectedFormatNumbers = inject('baseTableFormatNumbers', true);
const shouldFormatNumbers = computed(() => props.formatNumbers === null ? injectedFormatNumbers : props.formatNumbers);

const formatMaybeNumber = (val) => {
  if (val === null || val === undefined) return '';
  if (!shouldFormatNumbers.value) return String(val);

  // Числа (включая 0) форматируем через общий фильтр
  if (typeof val === 'number') {
    return val === 0 ? '0' : numberFormatter(val);
  }

  // Строки вида -?\d+(\.\d+)? форматируем, остальное оставляем как есть
  if (typeof val === 'string') {
    const s = val.trim();
    if (/^-?\d+(\.\d+)?$/.test(s)) {
      // конвертируем в число, чтобы корректно применить форматтер
      const asNum = Number(s);
      if (Object.is(asNum, -0)) return '0';
      return asNum === 0 ? '0' : numberFormatter(asNum);
    }
    return val;
  }

  return String(val);
}

function findTableParent(el) {
  if (!el || !el.parent) return null;
  if (el.parent.type && el.parent.type.name === 'ElTable') {
    return el.parent.uid;
  }
  return findTableParent(el.parent);
}

const parentId = findTableParent(instance);
const baseIdPart = parentId != null ? String(parentId) : null;
const rawColumnKey = props.columnId ?? attrs.prop ?? props.label;
const normalizedColumnKey = rawColumnKey != null ? String(rawColumnKey) : null;
const resolvedColumnId = baseIdPart && normalizedColumnKey
  ? `${baseIdPart}:${normalizedColumnKey}`
  : (baseIdPart ? `${baseIdPart}:${instance.uid}` : null);

const shouldRegisterColumn = !!resolvedColumnId && props.includeInColumns;
if (shouldRegisterColumn) {
  store.dispatch('Columns/actionSetColumn', {
    id: resolvedColumnId,
    label: props.label,
    parentId,
    tableId: parentId,
    defaultValue: props.defaultVisible,
  });
}

onBeforeUnmount(() => {
  if (shouldRegisterColumn) {
    store.dispatch('Columns/actionRemoveColumn', { id: resolvedColumnId });
  }
  store.dispatch('JSSSheetsManager/actionDetach', { id: instance.uid });
});

const visible = computed(() => {
  // Если колонка исключена из селектора, её видимость задаётся defaultVisible
  if (!props.includeInColumns) {
    return props.defaultVisible === 'always' ? true : !!props.defaultVisible;
  }
  if (!resolvedColumnId) {
    return props.defaultVisible === 'always' ? true : !!props.defaultVisible;
  }
  const val = store.getters['Columns/getColumnValue'](resolvedColumnId)
  // Фолбэк на defaultVisible, если колонка ещё не зарегистрирована
  if (val === undefined) {
    return props.defaultVisible === 'always' ? true : !!props.defaultVisible;
  }
  return val;
});

const defaultNodes = computed(() => (slots.default ? slots.default() : []));
const hasNestedColumns = computed(() =>
  defaultNodes.value.some(
    node => node.type && node.type.name === 'BaseTableColumn'
  )
);
</script>
